/**
 * Greenhouse Job Board API Integration
 * Fetches and displays job listings from Greenhouse
 *
 * API Documentation: https://developers.greenhouse.io/job-board.html
 */

class GreenhouseJobs extends HTMLElement {
  constructor() {
    super();
    this.boardToken = this.dataset.boardToken;
    /** @type {HTMLElement|null} */
    this.container = null;
    /** @type {HTMLElement|null} */
    this.loadingEl = null;
    /** @type {HTMLElement|null} */
    this.errorEl = null;
  }

  connectedCallback() {
    if (!this.boardToken) {
      console.error("Greenhouse: Missing board token");
      return;
    }

    this.container = /** @type {HTMLElement|null} */ (this.querySelector("[data-jobs-container]"));
    this.loadingEl = /** @type {HTMLElement|null} */ (this.querySelector("[data-jobs-loading]"));
    this.errorEl = /** @type {HTMLElement|null} */ (this.querySelector("[data-jobs-error]"));

    this.fetchJobs();
  }

  async fetchJobs() {
    const apiUrl = `https://boards-api.greenhouse.io/v1/boards/${this.boardToken}/jobs?content=true`;

    try {
      this.showLoading(true);
      this.showError(false);

      const response = await fetch(apiUrl);

      if (!response.ok) {
        throw new Error(`Failed to fetch jobs: ${response.status}`);
      }

      const data = await response.json();
      this.renderJobs(data.jobs);
    } catch (error) {
      console.error("Greenhouse API Error:", error);
      this.showError(true);
    } finally {
      this.showLoading(false);
    }
  }

  /**
   * @param {Array<{id: number, title: string, location?: {name: string}, absolute_url?: string, content?: string}>} jobs
   */
  renderJobs(jobs) {
    if (!this.container) return;

    if (!jobs || jobs.length === 0) {
      this.container.innerHTML = `
        <p class="text-center text-asphalt py-6">No open positions at this time.</p>
      `;
      return;
    }

    const jobsHtml = jobs.map(job => this.createJobCard(job)).join("");
    this.container.innerHTML = jobsHtml;
  }

  /**
   * @param {{id: number, title: string, location?: {name: string}, absolute_url?: string, content?: string}} job
   * @returns {string}
   */
  createJobCard(job) {
    const title = this.escapeHtml(job.title);
    const location = job.location?.name ? this.escapeHtml(job.location.name) : "";
    const url = this.escapeHtml(job.absolute_url || "#");

    // Strip HTML tags, truncate, and escape for safe insertion
    const description = job.content
      ? this.escapeHtml(this.stripHtml(job.content).trim().substring(0, 200)) + "..."
      : "";

    return `
      <a 
        href="${url}" 
        target="_blank"
        rel="noopener noreferrer"
        class="group block w-full py-6 border-b border-medium-smoke no-underline"
      >
        <h3 class="text-brunt-orange text-18-bold mb-1 inline-block border-b border-brunt-orange group-hover:border-transparent transition-colors">
          ${title}
        </h3>
        ${location ? `<p class="text-14-regular text-asphalt mb-1">${location}</p>` : ""}
        ${description ? `<p class="text-14-regular text-asphalt">${description}</p>` : ""}
      </a>
    `;
  }

  /**
   * @param {string} html
   * @returns {string}
   */
  stripHtml(html) {
    // Decode HTML entities first (Greenhouse encodes them)
    const textarea = document.createElement("textarea");
    textarea.innerHTML = html;
    const decoded = textarea.value;

    // Then strip all HTML tags
    const doc = new DOMParser().parseFromString(decoded, "text/html");
    return doc.body.textContent || "";
  }

  /**
   * @param {string} text
   * @returns {string}
   */
  escapeHtml(text) {
    const div = document.createElement("div");
    div.textContent = text;
    return div.innerHTML;
  }

  /**
   * @param {boolean} show
   */
  showLoading(show) {
    if (this.loadingEl) {
      this.loadingEl.style.display = show ? "block" : "none";
    }
  }

  /**
   * @param {boolean} show
   */
  showError(show) {
    if (this.errorEl) {
      this.errorEl.style.display = show ? "block" : "none";
    }
  }
}

if (!customElements.get("greenhouse-jobs")) {
  customElements.define("greenhouse-jobs", GreenhouseJobs);
}
